import { useState } from "react";
import { INVENTORY_KIND_LABELS, type AdminAccount, type AdminInventory } from "@iltocat/shared";
import { ApiError, api, bytesToGb, formatBytes, gbToBytes } from "../lib/api.js";
import { Notice } from "./Notice.js";
import { Panel } from "./Panel.js";

/**
 * Accounts: on or off, the quota each one is held to, the stock add-on, and
 * the inventories each one owns. The status lever is Stage 0; the quota
 * editor is Stage 5; the add-on and the inventory levers are Stage 7.
 *
 * Every lever here can be undone. An inventory can be renamed, archived and
 * restored from the console, but only its owner can delete it.
 */
export function AccountsPanel({
  accounts,
  onChange,
}: {
  accounts: AdminAccount[];
  onChange: (next: AdminAccount[]) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(id: string, work: () => Promise<{ accounts: AdminAccount[] }>) {
    setBusy(id);
    setError(null);
    try {
      onChange((await work()).accounts);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong.");
    } finally {
      setBusy(null);
    }
  }

  const toggle = (account: AdminAccount) =>
    run(account.id, () =>
      api.admin.setAccountStatus(account.id, account.status === "active" ? "disabled" : "active"),
    );

  const toggleStock = (account: AdminAccount) =>
    run(account.id, () => api.admin.setAccountStock(account.id, !account.stockEnabled));

  function setQuota(account: AdminAccount, event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const raw = String(new FormData(event.currentTarget).get("quota") ?? "");
    const quotaBytes = gbToBytes(raw);
    if (quotaBytes === null) {
      setError("That is not a storage limit.");
      return;
    }
    void run(account.id, () => api.admin.setAccountQuota(account.id, quotaBytes));
  }

  return (
    <Panel title="Accounts" note="Disabling takes effect on the account's next request.">
      {error && <Notice tone="error">{error}</Notice>}
      {accounts.length === 0 ? (
        <p className="muted">Nobody has signed in yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Address</th>
              <th>Status</th>
              <th>Storage used</th>
              <th>Limit (GB)</th>
              <th>Stock add-on</th>
              <th>Inventories</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.id}>
                <td data-label="Address">
                  <strong>{account.email}</strong>
                </td>
                <td data-label="Status">
                  <span className={account.status === "active" ? "pill pill-ok" : "pill pill-warn"}>
                    {account.status === "active" ? "Active" : "Disabled"}
                  </span>
                </td>
                <td data-label="Storage used" className="muted">
                  {formatBytes(account.usedBytes)}
                </td>
                <td data-label="Limit (GB)">
                  {/* Keyed on the quota itself so a successful save replaces the
                      draft with the server's new figure instead of leaving
                      whatever was mid-edit. */}
                  <form className="quota-form" onSubmit={(event) => setQuota(account, event)}>
                    <input
                      key={account.quotaBytes}
                      name="quota"
                      type="number"
                      min="0"
                      step="0.5"
                      className="input input-small input-quota"
                      defaultValue={bytesToGb(account.quotaBytes)}
                      disabled={busy === account.id}
                      aria-label={`Storage limit for ${account.email}, in gigabytes`}
                    />
                    <button
                      className="button button-ghost button-small"
                      type="submit"
                      disabled={busy === account.id}
                    >
                      Set
                    </button>
                  </form>
                </td>
                <td data-label="Stock add-on">
                  <button
                    className={`button button-small ${account.stockEnabled ? "button-on" : "button-ghost"}`}
                    type="button"
                    aria-pressed={account.stockEnabled}
                    disabled={busy === account.id}
                    onClick={() => void toggleStock(account)}
                  >
                    {account.stockEnabled ? "On" : "Off"}
                  </button>
                </td>
                <td data-label="Inventories">
                  <ul className="admin-inventories">
                    {account.inventories.map((inventory) => (
                      <AdminInventoryRow
                        key={inventory.id}
                        inventory={inventory}
                        busy={busy === inventory.id}
                        onRename={(name) =>
                          void run(inventory.id, () => api.admin.renameInventory(inventory.id, name))
                        }
                        onArchive={(archive) =>
                          void run(inventory.id, () => api.admin.archiveInventory(inventory.id, archive))
                        }
                      />
                    ))}
                  </ul>
                </td>
                <td className="cell-action">
                  <button
                    className={`button button-small ${account.status === "active" ? "button-danger" : "button-quiet"}`}
                    type="button"
                    disabled={busy === account.id}
                    onClick={() => void toggle(account)}
                  >
                    {account.status === "active" ? "Disable" : "Enable"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function AdminInventoryRow({
  inventory,
  busy,
  onRename,
  onArchive,
}: {
  inventory: AdminInventory;
  busy: boolean;
  onRename: (name: string) => void;
  onArchive: (archive: boolean) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const archived = inventory.archivedAt !== null;
  const label = inventory.name || (inventory.home ? "Home" : "Unnamed");

  if (draft !== null) {
    return (
      <li className="admin-inventory">
        <form
          className="quota-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim()) onRename(draft.trim());
            setDraft(null);
          }}
        >
          <input
            className="input input-small"
            aria-label={`New name for ${label}`}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <button className="button button-ghost button-small" type="submit" disabled={busy || !draft.trim()}>
            Save
          </button>
        </form>
      </li>
    );
  }

  return (
    <li className="admin-inventory">
      <span>
        {label}
        <span className="muted">
          {" "}
          · {INVENTORY_KIND_LABELS[inventory.kind]} · {inventory.members}{" "}
          {inventory.members === 1 ? "member" : "members"}
          {archived && " · archived"}
        </span>
      </span>
      <span className="admin-inventory-tools">
        <button
          className="button button-ghost button-small"
          type="button"
          disabled={busy}
          onClick={() => setDraft(inventory.name)}
        >
          Rename
        </button>
        {!inventory.home && (
          <button
            className="button button-ghost button-small"
            type="button"
            disabled={busy}
            onClick={() => onArchive(!archived)}
          >
            {archived ? "Restore" : "Archive"}
          </button>
        )}
      </span>
    </li>
  );
}
